import { useState } from "react";
import {
  Save,
  Plus,
  Mail,
  CreditCard,
  Bell,
  Globe,
  Palette,
  Shield,
  Clock,
} from "lucide-react";

import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  DynamicFieldRenderer,
  Switch,
  DataTable,
  AdminuserTableConfig,
  generalFields,
  regionalFields,
  brandingFields,
  users,
  permissions,
} from "@b2b/ui-components";

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState("general");

  return (
    <div className="space-y-8 mt-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground">
            Configure your marketplace platform settings and preferences
          </p>
        </div>
        <Button variant="primary">
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-5 rounded-md overflow-hidden">
          {["general", "users", "permissions", "billing", "notifications"].map(
            (tab) => {
              const labelMap: Record<string, string> = {
                general: "General",
                users: "User Management",
                permissions: "Permissions",
                billing: "Payment & Billing",
                notifications: "Notifications",
              };

              return (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className={`font-medium text-center   transition-colors duration-200 ${
                    activeTab === tab
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {labelMap[tab]}
                </TabsTrigger>
              );
            }
          )}
        </TabsList>
        <TabsContent value="general" className="space-y-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Company Info */}
            <Card className="flex-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="h-5 w-5 text-orange-500" />
                  <span>Company Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DynamicFieldRenderer fields={generalFields} />
              </CardContent>
            </Card>

            {/* Regional Settings */}
            <Card className="flex-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <span>Regional Settings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DynamicFieldRenderer fields={regionalFields} />
              </CardContent>
            </Card>
          </div>

          {/* Full width: Branding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Palette className="h-5 w-5 text-orange-500" />
                <span>Branding</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DynamicFieldRenderer fields={brandingFields} />
            </CardContent>
          </Card>
        </TabsContent>
        {/* User Management (table stays as-is) */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">System Users</h3>
            <Button variant="primary">
              <Plus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </div>

          {/* ✅ Using custom DataTable instead of shadcn table */}
          <DataTable
            data={users}
            config={AdminuserTableConfig}
            rowsPerPage={5}
          />
        </TabsContent>

        <TabsContent value="permissions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-orange-500" />
                <span>Role-Based Access Control</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">
                      Admin Role
                    </h4>
                    {permissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">
                      Manager Role
                    </h4>
                    {permissions.slice(0, 3).map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                    {permissions.slice(3).map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                        <Switch />
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">
                      Viewer Role
                    </h4>
                    {permissions.slice(0, 2).map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                    {permissions.slice(2).map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                        <Switch />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* Notifications */}
        <TabsContent value="billing" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-orange-500" />
                  <span>Payment Methods</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-8 bg-orange-100 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-orange-500">
                        VISA
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">•••• •••• •••• 1234</p>
                      <p className="text-sm text-muted-foreground">
                        Expires 12/25
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-700 hover:bg-green-600 text-white">
                    Primary
                  </Badge>
                </div>
                <Button variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subscription Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Plan</span>
                    <Badge className="bg-orange-600 hover:bg-orange-500 text-white">
                      Enterprise
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Monthly Cost
                    </span>
                    <span className="font-semibold">$299/month</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Next Billing
                    </span>
                    <span className="font-semibold">Feb 15, 2024</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  View Billing History
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="notifications" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <span>Email Notifications</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">Order Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Get notified about new orders
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">User Registrations</p>
                    <p className="text-sm text-muted-foreground">
                      New user sign-ups
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">System Alerts</p>
                    <p className="text-sm text-muted-foreground">
                      Important system notifications
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">Weekly Reports</p>
                    <p className="text-sm text-muted-foreground">
                      Automated weekly summaries
                    </p>
                  </div>
                  <Switch />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <span>Push Notifications</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">Browser Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Show notifications in browser
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">Mobile Push</p>
                    <p className="text-sm text-muted-foreground">
                      Mobile app notifications
                    </p>
                  </div>
                  <Switch />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notification-frequency">
                    Notification Frequency
                  </Label>
                  <Select>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem
                        value="immediate"
                        className="hover:bg-gray-100"
                      >
                        Immediate
                      </SelectItem>
                      <SelectItem value="hourly" className="hover:bg-gray-100">
                        Hourly Digest
                      </SelectItem>
                      <SelectItem value="daily" className="hover:bg-gray-100">
                        Daily Digest
                      </SelectItem>
                      <SelectItem value="weekly" className="hover:bg-gray-100">
                        Weekly Digest
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
